"use client"

// =============================================================================
// ws-provider.tsx — WebSocket provider (replaces sse-provider.tsx).
//
// Single long-lived WebSocket carries every server→client event: ticks (batched
// and single-shot), order-update, user-update, notification, heartbeat, auth-
// expired. subscribe/unsubscribe are JSON messages on the SAME socket — no
// extra HTTP round-trips — which structurally eliminates the SSE-era 404
// race-on-disconnect (there's no cross-request connId to mis-resolve).
//
// Wire shape:
//   Server → Client: { "type": "<kind>", ...payload }
//   Client → Server: { "type": "subscribe"|"unsubscribe"|"ping", "tokens": [...], "request_id": "..." }
//
// Same context API as the previous SSEProvider so existing consumers
// (useMarketData, useNotifications, notification-provider) only need an
// import change.
// =============================================================================

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react"
import { apiFetch, apiV1Fetch, triggerSessionExpiry } from "@/lib/api"
import { noteWsClose, noteWsGaveUp } from "@/lib/net-journal"

export interface MarketTick {
  symbol: string
  token: number
  ltp: number
  bid: number
  ask: number
  open: number
  high: number
  low: number
  prevClose: number
  volume: number
  time: number
  change: number
  lastTradedQty: number
  atp: number
  oi: number
  bidQty: number
  askQty: number
  lowerCircuit: number
  upperCircuit: number
  // Forex only (issue-650): display Day's / Week's range. For forex, high/low
  // above is the weekly watermark range; 0 on every other segment.
  dayHigh?: number
  dayLow?: number
  weekHigh?: number
  weekLow?: number
}

/**
 * Terminal states a payment_orders row can reach, as pushed over the wire by
 * markPaymentTerminal / reconcileCompleted in backend payment.controller.js.
 *
 * Exported so the funds page, the pending-payment banner and the Add Funds
 * dialog all narrow on the SAME union. This matters more than it looks: the
 * 'payment_status_updated' handler below silently DROPS any status not listed
 * here, so a member missing from this type surfaces as "the dialog never
 * leaves the spinner" rather than as a compile error.
 *
 * 'cancelled' is the payee deliberately abandoning a live payment link via
 * POST /api/payment/:refId/cancel — distinct from 'expired', where they simply
 * walked away and the clock ran out.
 */
export type PaymentEventStatus = 'completed' | 'failed' | 'expired' | 'cancelled'

interface WSProviderState {
  lastOrderUpdate: number
  // Bumped by 'balance_updated' user-updates (fires every margin-daemon cycle,
  // ~1 Hz). Screens that show balance/funds watch this; screens that re-fetch
  // heavier resources (orders, positions) intentionally exclude it.
  lastUserUpdate: number
  // Bumped only by 'config_updated' user-updates (admin/broker edited the
  // trader's profile/risk/segment/limit/auto-square-off settings). Rare —
  // emits once per admin save — so every screen that displays config-derived
  // data can safely subscribe without flooding.
  lastConfigUpdate: number
  // Push-delivered payment-gateway status change. Set by 'payment_status_updated'
  // user-updates fired from the payment webhook handler (and reconciliation cron).
  // The funds page's pending-banner and the Add Funds dialog react to changes
  // here so neither needs to poll /api/payment/:refId/status.
  lastPaymentEvent: { reference_id: string; status: PaymentEventStatus; amount: number; ts: number } | null
  // Monotonic tick bumped on ANY parent-child topup lifecycle event:
  //   child_topup_initiated, own_topup_initiated, child_topup_settled,
  //   payment_status_updated. The PendingTopupsBanner subscribes to this
  //   value and re-fetches /api/payment/pending-topups on each bump so
  //   both payer and payee sides stay live without polling.
  lastTopupEventTs: number
  // Monotonic tick bumped on ANY trader manual-deposit / withdrawal request
  // lifecycle event (submitted, approved, rejected, cancelled), delivered to
  // BOTH the trader's direct broker and — when oversight is enabled — the
  // tenant admin. The two request queues on /admin/payment and their
  // approve/reject dialogs subscribe to this so that when one of the two
  // entitled actors takes an action, the other's open screen reconciles
  // immediately instead of showing a row that is no longer actionable.
  //
  // Replaces the previous mechanism, which sniffed notification TITLES
  // (`title.includes("manual deposit")`) — far too fragile to carry a
  // two-actor correctness guarantee, since any copy edit silently broke it.
  lastRequestQueueEventTs: number
  // Detail of the most recent request-queue event, so an OPEN approve/reject
  // dialog can tell whether the row it is showing is the one that just got
  // actioned out from under it.
  lastRequestQueueEvent: {
    kind: 'manual_deposit' | 'withdrawal' | string
    request_id: number | null
    status: string | null
    ts: number
  } | null
  lastNotification: any
  connId: string | null
  isConnected: boolean
  isConnecting: boolean
  retryCount: number
  scripMapReady: boolean
  scripMapError: boolean
  // Bumped on every successful scrip-map fetch and on injectScrips(). Because
  // `allScrips` / `scripTokenMap` are exposed as refs (stable references),
  // consumers must include this in their memo deps to recompute when the
  // underlying arrays change.
  scripMapVersion: number
}

// External-store handle for tick subscriptions. Identity is stable for the
// lifetime of the provider — safe to put in context and useSyncExternalStore.
//
// Why this exists: previously the provider held a `tickHash` state field and
// bumped it every 200 ms, forcing every useWS() consumer (notifications,
// status bars, etc.) to re-render at 5 Hz regardless of whether they read
// ticks. Now ticks live outside React state; only subscribers via this store
// wake up, and `useTick(token)` wakes only for that token's updates.
export interface TickStore {
  // Per-token subscription — cb fires when this token's tick changes (after
  // the 200 ms throttle window). useSyncExternalStore feeds this.
  //
  // Accepts number OR string for `token` because Postgres stores
  // `zerodha_instrument_token` as VARCHAR — the API returns it as a string,
  // and the wire ticks arrive as numbers. The implementation normalizes both
  // shapes to the same numeric key before touching the listener map.
  subscribe: (token: number | string, cb: () => void) => () => void
  getSnapshot: (token: number | string) => MarketTick | undefined
  // Global "any tick changed" subscription — fires once per throttled batch.
  // Backs the legacy `tickHash` field returned by useMarketData() so existing
  // memo-deps continue to behave the same.
  subscribeAny: (cb: () => void) => () => void
  getAnySnapshot: () => number
  // Raw map reference — stable identity. Read-only from consumers.
  ticks: Record<number, MarketTick>
}

interface WSContextType extends WSProviderState {
  ticks: Record<number, MarketTick>
  scripTokenMap: Record<string, number>
  allScrips: any[]
  tickStore: TickStore
  refetchScrips: () => Promise<void>
  // Merge a small set of scrips (deduped by scrip_id) into the provider's
  // canonical list and refresh the token-map. Used by toggleWatchlist when the
  // user adds a scrip that wasn't in the initial limit=5000 fetch — keeps the
  // override in the provider (single source of truth) instead of dashboard-
  // local state, so it survives navigation/unmount.
  injectScrips: (scrips: any[]) => void
  subscribeTokens: (tokens: number[]) => void
  unsubscribeTokens: (tokens: number[]) => void
  reconnect: (opts?: { force?: boolean }) => void
}

const WSContext = createContext<WSContextType | undefined>(undefined)

// ─── tuning constants ───
const INITIAL_RETRY_DELAY = 1000
const MAX_RETRY_DELAY = 30000
// Attempt count after which we STOP escalating the backoff. We do NOT stop
// reconnecting.
//
// This used to be a hard give-up: at 5 attempts (≈31 s) the provider called
// triggerSessionExpiry({reason:"ws-max-retries"}), which clears
// localStorage.user and redirects to /trade/login. That meant a ~31-second
// connectivity gap logged a trader out — with a valid session cookie, without
// the server ever returning a 401, mid-position. It also set a sticky flag
// that made connect() refuse forever after, so the socket could not come back
// even once the network did; the only re-arm paths were the `online` event
// (which never fires when navigator.onLine never went false) and a
// foreground/background cycle. sank09's socket was dead from 16:15 to 16:34
// for exactly this reason.
//
// "Cannot reach the server" and "session is invalid" are different claims and
// only the server can make the second one. A 401 on the probe below, or a
// close code 4001, still expires the session immediately — that path is
// untouched. Absent either, we keep retrying at MAX_RETRY_DELAY for as long as
// the tab is alive.
const RECONNECT_BACKOFF_CEILING_ATTEMPTS = 5
// Re-ask "is my session still valid?" every N consecutive reconnect failures.
// See the callsite for why a one-shot probe is not enough now that the loop
// runs indefinitely.
const SESSION_PROBE_EVERY_N = 10
// Server sends app-level heartbeats every 15 s; allow one missed beat before
// we presume the underlying TCP is silently dead.
const HEARTBEAT_TIMEOUT = 20000
const SCRIP_CACHE_KEY = "scrip_map_cache"
const STALE_AFTER_HIDDEN_MS = 2 * 60 * 1000
// Max age of the last received tick before we treat the socket as silently
// dead. Used by the tab-visibility handler so a returning user doesn't sit on
// frozen prices waiting for the 20s heartbeat watchdog to fire. Mock-ticks
// arrive ~1.5 Hz per token and live ticks burst much faster, so 10s is a
// comfortable ceiling — any gap larger than that means the feed is broken.
const TICK_FRESHNESS_MS = 10000
const MAX_TICK_CACHE = 5000

// ─── per-token freshness watchdog (gap-confirmed) ───
// A single token can go silent while the socket and every other token keep
// streaming (the tya1 incident: one option froze on a live connection). The
// 5s resync uses these thresholds to detect and silently recover that case.
//   PER_TOKEN_STALE_MS   — a subscribed+acked token with no tick for this long
//                          is a resubscribe candidate. Live ticks burst far
//                          faster; even illiquid scrips update well inside 8s
//                          during market hours.
//   STREAM_ACTIVE_MS     — only act when SOME token ticked this recently, i.e.
//                          the feed is provably alive. This is what makes it
//                          "gap-confirmed" rather than a dumb timeout, so a
//                          quiet market (nothing ticking) never triggers churn.
//   RESUB_THROTTLE_MS    — per-token floor between silent resubscribes so a
//                          genuinely-quiet-but-alive token isn't re-sent every
//                          resync tick.
const PER_TOKEN_STALE_MS = 8000
const STREAM_ACTIVE_MS = 2000
const RESUB_THROTTLE_MS = 30000

// ─── demand-driven connect ───
// Floor between reconnects triggered by *subscription demand* (see
// requestConnectForDemand). The onclose retry loop's exponential backoff still
// governs a genuinely unreachable server; this throttle only bounds how often
// user demand is allowed to short-circuit it. Without it, the 5s resync
// re-sending a subscribe that keeps failing would turn into a connect attempt
// every 5s per disconnected trader — a fleet-wide storm against a server that
// is, by hypothesis, already struggling.
const DEMAND_RECONNECT_THROTTLE_MS = 10000

// localStorage cache for last-known ticks of CURRENTLY-SUBSCRIBED tokens
// only — gives the watchlist a real LTP on first paint after a hard reload
// (the "Try Again" path on the Connection Lost modal, or a PWA cold launch)
// instead of "—" flashes while the WS reconnects and the first tick batch
// arrives. Limited to subscribed tokens so the persisted blob stays small
// (≤ 250 watchlist items × ~150 bytes ≈ 37 KB max). Key is per-user-id so
// switched accounts never see the previous trader's prices.
const TICK_CACHE_KEY_PREFIX = "tick_cache_"
// Throttle persists to ≤ 1 Hz so the 200 ms tick batch handler doesn't
// thrash localStorage at 5 writes per second.
const TICK_PERSIST_THROTTLE_MS = 1000

function getCurrentUserIdForTickCache(): string | null {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem("user")
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed?.id != null ? String(parsed.id) : null
  } catch {
    return null
  }
}

function readTickCache(): Record<number, MarketTick> | null {
  const userId = getCurrentUserIdForTickCache()
  if (!userId) return null
  try {
    const raw = window.localStorage.getItem(`${TICK_CACHE_KEY_PREFIX}${userId}`)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.ticks || typeof parsed.ticks !== "object") return null
    // Normalize keys back to numbers — JSON serialises object keys as strings.
    const out: Record<number, MarketTick> = {}
    for (const [k, v] of Object.entries(parsed.ticks as Record<string, MarketTick>)) {
      const tokenNum = Number(k)
      if (Number.isFinite(tokenNum) && tokenNum > 0 && v && typeof v === "object") {
        out[tokenNum] = v
      }
    }
    return out
  } catch {
    return null
  }
}

function writeTickCache(ticks: Record<number, MarketTick>, subscribedTokens: Set<number>): void {
  const userId = getCurrentUserIdForTickCache()
  if (!userId) return
  // Only persist ticks for tokens we currently have a subscription intent
  // for. Stops the blob growing for tokens the trader passed through during
  // search-browse but never added to their watchlist.
  const filtered: Record<number, MarketTick> = {}
  for (const token of subscribedTokens) {
    const t = ticks[token]
    if (t) filtered[token] = t
  }
  try {
    window.localStorage.setItem(
      `${TICK_CACHE_KEY_PREFIX}${userId}`,
      JSON.stringify({ ticks: filtered, ts: Date.now() })
    )
  } catch {
    /* quota / private-mode — non-fatal, in-memory ticksRef still drives UI */
  }
}

// ─── scrip-map helpers (module scope so they are not re-created per render
// and, more importantly, are reachable from callbacks rather than trapped
// inside a single mount-time effect closure) ───

const SCRIPS_URL = "/api/admin/scrips?show_enabled_only=true&limit=5000&market=ALL&segment=ALL"

// Auth presence is a POINT-IN-TIME question, so it must be asked at call time.
// Reading it once at provider mount is what previously stranded the scrip map
// forever for traders whose localStorage hint was absent at that instant.
function hasTraderAuth(): boolean {
  if (typeof window === "undefined") return false
  try {
    return !!localStorage.getItem("user")
  } catch {
    return false
  }
}

function isSuperadminPath(): boolean {
  if (typeof window === "undefined") return false
  return window.location.pathname.startsWith("/superadmin")
}

function buildScripMap(data: any[]): Record<string, number> {
  const map: Record<string, number> = {}
  data.forEach((s: any) => {
    if (s.zerodha_instrument_token) {
      const token =
        typeof s.zerodha_instrument_token === "number"
          ? s.zerodha_instrument_token
          : parseInt(s.zerodha_instrument_token, 10)
      if (!isNaN(token)) {
        map[s.scrip_id] = token
        map[s.scrip_id.toUpperCase().trim()] = token
      }
    }
  })
  return map
}

function persistScripCache(map: Record<string, number>, allScrips: any[]): void {
  try {
    localStorage.setItem(SCRIP_CACHE_KEY, JSON.stringify({ map, allScrips, ts: Date.now() }))
  } catch {
    /* storage full — ignore */
  }
}

// Stale-while-revalidate read: ANY parseable blob is usable, fresh or not.
// Returns null when there is nothing worth painting.
function readScripCache(): { map: Record<string, number>; allScrips: any[] } | null {
  try {
    const raw = localStorage.getItem(SCRIP_CACHE_KEY)
    if (!raw) return null
    const { map, allScrips } = JSON.parse(raw)
    const usable =
      map && typeof map === "object" && Array.isArray(allScrips) && allScrips.length > 0
    return usable ? { map, allScrips } : null
  } catch {
    return null
  }
}

// Compute the WebSocket URL. Prefers NEXT_PUBLIC_WS_URL (set in dev to point
// at the docker service-ws on :5003) and falls back to same-host auto-detect
// (used in prod where nginx proxies wss://<host>/api/ws → service-ws:5003).
//
// When an explicit URL is set, we KEEP its port + path but swap the hostname
// to whatever the page is currently on. This is critical for per-domain
// dev/staging: if the page is at admin1.test.local:3000 but explicit is
// `ws://localhost:5003/api/ws`, a literal cross-origin WS to localhost only
// sees `localhost`-scoped cookies — the trader's session cookie lives on
// `admin1.test.local`, so the upgrade returns 401 and the WSProvider's
// reconnect loop eventually surfaces "Session expired". By rewriting the
// hostname to match the page, the upgrade carries the right cookie. In
// vanilla localhost dev this is a no-op (hostname is already `localhost`).
// In prod NEXT_PUBLIC_WS_URL is unset so the fallback branch already uses
// `window.location.host`.
function buildWsUrl(requestId: string): string {
  if (typeof window === "undefined") return ""
  const explicit = process.env.NEXT_PUBLIC_WS_URL
  let base: string
  if (explicit) {
    try {
      const u = new URL(explicit)
      u.hostname = window.location.hostname
      base = u.toString()
    } catch {
      base = explicit
    }
  } else {
    base = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/api/ws`
  }
  // The browser doesn't let us set custom headers on `new WebSocket()`, so we
  // pass the request_id as a query param. The server side reads it (later) to
  // seed AsyncLocalStorage if needed — today it's just for log correlation.
  const sep = base.includes("?") ? "&" : "?"
  return `${base}${sep}request_id=${encodeURIComponent(requestId)}`
}

export function WSProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<WSProviderState>({
    lastOrderUpdate: 0,
    lastUserUpdate: 0,
    lastConfigUpdate: 0,
    lastPaymentEvent: null,
    lastTopupEventTs: 0,
    lastRequestQueueEventTs: 0,
    lastRequestQueueEvent: null,
    lastNotification: null,
    connId: null,
    isConnected: false,
    isConnecting: false,
    retryCount: 0,
    scripMapReady: false,
    scripMapError: false,
    scripMapVersion: 0,
  })

  // ─── refs ───
  const wsRef = useRef<WebSocket | null>(null)
  const ticksRef = useRef<Record<number, MarketTick>>({})
  const scripTokenMapRef = useRef<Record<string, number>>({})
  const allScripsRef = useRef<any[]>([])
  // Coalesces concurrent scrip fetches onto ONE in-flight request. Mount,
  // bottom-tab remount, pull-to-refresh, WS-connect and visibility re-arm can
  // all fire inside the same window; a second caller awaits the SAME promise
  // rather than resolving early (which would end a pull-to-refresh spinner
  // before the data actually landed).
  const scripFetchPromiseRef = useRef<Promise<void> | null>(null)
  // Tick-cache hydration must run exactly once per provider mount. Re-running
  // it after ticks start flowing would overwrite fresh in-memory prices with
  // stale persisted ones.
  const tickCacheHydratedRef = useRef(false)

  // ─── tick store bookkeeping (external-store pattern) ───
  // Per-token listeners — keyed by token, value is a set of useSyncExternalStore
  // callbacks. Flushed on the 200 ms throttle so we don't fan out 100s of
  // renders per second.
  const tickListenersRef = useRef<Map<number, Set<() => void>>>(new Map())
  // Diagnostic-only helper. Enable in browser console:
  //   window.__WS_DEBUG__ = true             // log all subscribe/unsubscribe lifecycle
  //   window.__WS_DEBUG_TOKEN__ = 31447052   // also log per-token tick activity
  // Remove after diagnosis.
  const dbg = (...args: any[]) => {
    if (typeof window !== "undefined" && (window as any).__WS_DEBUG__) {
      console.log("[WSDBG]", ...args)
    }
  }
  const dbgToken = (token: number, ...args: any[]) => {
    if (typeof window !== "undefined" && (window as any).__WS_DEBUG_TOKEN__ === token) {
      console.log(`[WSDBG ${token}]`, ...args)
    }
  }
  // Global "any tick" listeners — backs the legacy tickHash returned by
  // useMarketData. One subscriber per useMarketData() call.
  const anyTickListenersRef = useRef<Set<() => void>>(new Set())
  // Monotonic counter incremented each time the throttle flushes. Returned by
  // tickStore.getAnySnapshot() so useSyncExternalStore detects "changed".
  const anyTickVersionRef = useRef<number>(0)
  // Tokens whose tick changed since the last flush — drained on each throttle
  // fire so only affected per-token listeners run.
  const pendingTokenNotifyRef = useRef<Set<number>>(new Set())

  // stateRef mirrors `state` so callbacks captured at render time can read the
  // *current* connection status without taking `state.connId` / `state.isConnected`
  // as a dep. That keeps subscribeTokens / unsubscribeTokens identity stable,
  // which is what makes the memoized context value actually stable across
  // notification / order-event re-renders.
  const stateRef = useRef(state)
  useEffect(() => {
    stateRef.current = state
  }, [state])

  // Subscription bookkeeping — same model as the SSE provider:
  //   subscribedTokensRef       = "we want these subscribed" (intent)
  //   pendingSubscriptionsRef   = "in flight, no ack yet"
  //   successfullySubscribedRef = "server ack'd subscribe"
  //   rejectedTokensRef         = "admin disabled this scrip; don't retry"
  //   tokenRefCountRef          = per-token consumer count. Multiple consumers
  //     (watchlist + portfolio + trades) can subscribe to the same token; we
  //     only send {type:"unsubscribe"} to the server when the count drops to 0.
  //     Without this, removing a scrip from the watchlist unsubscribes its
  //     token globally even if portfolio/trades still display it — freezing
  //     the LTP for that scrip until a route remount re-subscribes.
  const subscribedTokensRef = useRef<Set<number>>(new Set())
  const pendingSubscriptionsRef = useRef<Set<number>>(new Set())
  const successfullySubscribedRef = useRef<Set<number>>(new Set())
  const rejectedTokensRef = useRef<Set<number>>(new Set())
  const tokenRefCountRef = useRef<Map<number, number>>(new Map())
  // Wall-clock of the last received tick (or any inbound frame). Used as a
  // freshness signal: on tab-visibility-restore we force-reconnect when this
  // is older than TICK_FRESHNESS_MS even if WebSocket.readyState reads OPEN,
  // because Chrome leaves silently-dead sockets in OPEN state until TCP times
  // out (minutes).
  const lastTickAtRef = useRef<number>(0)
  // Throttle for the 5s-resync's periodic retry of previously-rejected tokens
  // (covers admin-disable-then-re-enable mid-session without page refresh).
  const lastRejectedRetryAtRef = useRef<number>(0)
  // Per-token wall-clock of the last tick seen for that token. Backs the
  // gap-confirmed freshness watchdog: the app-wide lastTickAtRef resets on ANY
  // inbound tick, so it structurally can't spot one silent token while others
  // keep ticking (the tya1 freeze). This map lets the 5s resync detect a
  // single stalled token and silently re-subscribe it. Keyed by numeric token.
  const perTokenLastTickRef = useRef<Map<number, number>>(new Map())
  // Per-token throttle for that silent re-subscribe so a genuinely illiquid
  // (quiet-but-alive) token isn't resubscribed more than once per window.
  const lastResubAtRef = useRef<Map<number, number>>(new Map())

  // Correlation table for in-flight subscribe / unsubscribe requests so we
  // can update bookkeeping when the ack message arrives.
  type PendingReq = { kind: "subscribe" | "unsubscribe"; tokens: number[] }
  const pendingReqsRef = useRef<Map<string, PendingReq>>(new Map())

  const [retryTrigger, setRetryTrigger] = useState(0)
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const watchdogTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const throttleTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectDebounceRef = useRef<NodeJS.Timeout | null>(null)
  // Wall-clock of the last localStorage tick-cache write — gates the
  // throttled persist call inside flushTickNotifications to ≤ 1 Hz.
  const lastTickPersistAtRef = useRef<number>(0)

  const lastOrderEventHash = useRef<string>("")
  const lastUserEventHash = useRef<string>("")
  const sessionProbedRef = useRef(false)
  // Once the session is known to be expired (either via session-expired event
  // from api.ts or our own give-up path) we stop all reconnect attempts to
  // prevent the visible "WS request storm" in DevTools while the user is
  // stuck on a page that no longer has a valid cookie.
  const sessionExpiredRef = useRef(false)
  // Drives the banner copy + the React tree so the user sees "Session expired"
  // instead of the misleading "Market data unavailable" banner.
  const [sessionExpired, setSessionExpired] = useState(false)

  // ─── scrip map fetch ───
  //
  // Every entry point below re-checks auth at CALL time. The previous shape
  // read `localStorage.user` inside a []-dep effect and returned early when it
  // was absent, which permanently stranded the provider: the effect could
  // never run again, `scripMapReady` stayed false for the life of the tab, and
  // `refetchScrips` kept invoking a no-op default ref. A trader entering via
  // the /trade gate — localStorage hint evicted or cleared while the session
  // cookie was still valid — therefore sat on an infinite watchlist spinner
  // having issued ZERO /api/admin/scrips requests, while the WebSocket (whose
  // effect DOES re-run on every retry) reconnected happily. Auth presence is a
  // point-in-time question, so it is now asked at every call instead of once.

  // Hydrate last-known ticks for previously-subscribed tokens from
  // localStorage so the watchlist paints real LTPs on first frame instead of
  // "—". Guarded to run at most once per provider mount: re-running it after
  // the socket starts delivering ticks would overwrite fresh prices with stale
  // persisted ones. Individual tokens that already have a live tick are never
  // clobbered, so a late hydrate (auth arriving after mount) is still safe.
  const hydrateTickCacheOnce = useCallback(() => {
    if (tickCacheHydratedRef.current) return
    if (!hasTraderAuth()) return
    tickCacheHydratedRef.current = true
    const hydratedTicks = readTickCache()
    if (!hydratedTicks) return
    for (const [k, v] of Object.entries(hydratedTicks)) {
      const tokenNum = Number(k)
      if (Number.isFinite(tokenNum) && tokenNum > 0 && !ticksRef.current[tokenNum]) {
        ticksRef.current[tokenNum] = v
      }
    }
  }, [])

  const fetchScripsFromServer = useCallback((): Promise<void> => {
    if (!hasTraderAuth() || isSuperadminPath()) return Promise.resolve()
    // Coalesce: hand back the in-flight promise so callers await real
    // completion rather than resolving instantly alongside it.
    if (scripFetchPromiseRef.current) return scripFetchPromiseRef.current

    const run = async () => {
      for (let attempt = 1; attempt <= 5; attempt++) {
        try {
          const res = await apiFetch(SCRIPS_URL, { skipErrorNotification: true })
          const data = await res.json()
          if (data.success) {
            const map = buildScripMap(data.data)
            scripTokenMapRef.current = map
            allScripsRef.current = Array.isArray(data.data) ? data.data : []
            persistScripCache(map, allScripsRef.current)
            setState((prev) => ({
              ...prev,
              scripMapReady: true,
              // Clear any prior failure. `scripMapError` previously latched
              // true for the life of the tab, so a recovered fetch still left
              // consumers rendering the error/empty branch.
              scripMapError: false,
              scripMapVersion: prev.scripMapVersion + 1,
            }))
            return
          }
        } catch (err) {
          console.error(`[WSProvider] Scrip fetch attempt ${attempt}/5 failed:`, err)
        }
        if (attempt < 5) {
          await new Promise((r) => setTimeout(r, Math.min(1000 * 2 ** (attempt - 1), 16000)))
        }
      }
      console.error("[WSProvider] Scrip map fetch failed after 5 attempts; market data unavailable")
      setState((prev) => ({ ...prev, scripMapReady: true, scripMapError: true }))
    }

    const p = run().finally(() => {
      scripFetchPromiseRef.current = null
    })
    scripFetchPromiseRef.current = p
    return p
  }, [])

  // Stale-while-revalidate: paint ANY parseable cache blob (fresh or stale)
  // immediately and refresh behind it; only a cache miss awaits the network.
  // Keeps the "phone locks >5 min, unlocks, cellular still warming up" case
  // (prod fingerprints 5c11c7c… / 8b448bcee…) showing last-known scrips
  // instead of an empty watchlist behind a Connection Lost dialog.
  const loadScripMap = useCallback(async (): Promise<void> => {
    if (!hasTraderAuth() || isSuperadminPath()) return
    const cached = readScripCache()
    if (cached) {
      scripTokenMapRef.current = cached.map
      allScripsRef.current = cached.allScrips
      setState((prev) => ({
        ...prev,
        scripMapReady: true,
        scripMapError: false,
        scripMapVersion: prev.scripMapVersion + 1,
      }))
      void fetchScripsFromServer()
      return
    }
    await fetchScripsFromServer()
  }, [fetchScripsFromServer])

  // Idempotent re-arm. Cheap enough to call on every visibility / focus /
  // online event and on each WS connect confirmation — it no-ops the moment
  // the map is healthy, and otherwise recovers a provider that mounted before
  // auth was available.
  const ensureScripMapLoaded = useCallback(() => {
    if (!hasTraderAuth() || isSuperadminPath()) return
    hydrateTickCacheOnce()
    const { scripMapReady, scripMapError } = stateRef.current
    if (scripMapReady && !scripMapError) return
    void loadScripMap()
  }, [hydrateTickCacheOnce, loadScripMap])

  useEffect(() => {
    hydrateTickCacheOnce()
    // Deferred one microtask so the cache-hit setState lands in a callback
    // rather than synchronously in the effect body (react-hooks/set-state-in
    // -effect). Microtasks flush before the browser paints and effects already
    // run after the first render, so this costs the trader no extra frame.
    void Promise.resolve().then(loadScripMap)
  }, [hydrateTickCacheOnce, loadScripMap])

  // Always performs the real fetch. This previously called a ref whose default
  // value was a silent no-op, so the dashboard's per-mount refresh (bottom-tab
  // switch) and pull-to-refresh both did nothing whenever the mount-time
  // effect had bailed — the two paths a stuck trader would naturally try.
  const refetchScrips = useCallback(async () => {
    hydrateTickCacheOnce()
    await fetchScripsFromServer()
  }, [fetchScripsFromServer, hydrateTickCacheOnce])

  // Merge a small set of newly-known scrips into the canonical list. Used by
  // the dashboard's toggleWatchlist fallback when the user adds a scrip that
  // wasn't in the initial limit=5000 fetch (large option chains). Dedupes on
  // scrip_id, extends scripTokenMap so subscribeTokens can resolve them, and
  // bumps scripMapVersion so consumers re-render.
  const injectScrips = useCallback((scrips: any[]) => {
    if (!Array.isArray(scrips) || scrips.length === 0) return
    const known = new Set(allScripsRef.current.map((s) => s.scrip_id))
    const additions = scrips.filter((s) => s && s.scrip_id && !known.has(s.scrip_id))
    if (additions.length === 0) return
    allScripsRef.current = [...allScripsRef.current, ...additions]
    additions.forEach((s) => {
      if (s.zerodha_instrument_token) {
        const token =
          typeof s.zerodha_instrument_token === "number"
            ? s.zerodha_instrument_token
            : parseInt(s.zerodha_instrument_token, 10)
        if (!isNaN(token)) {
          scripTokenMapRef.current[s.scrip_id] = token
          scripTokenMapRef.current[s.scrip_id.toUpperCase().trim()] = token
        }
      }
    })
    // Refresh the persisted cache so a navigation/refresh within the TTL
    // window still sees the injected rows.
    try {
      localStorage.setItem(
        SCRIP_CACHE_KEY,
        JSON.stringify({
          map: scripTokenMapRef.current,
          allScrips: allScripsRef.current,
          ts: Date.now(),
        })
      )
    } catch {
      /* storage full — ignore */
    }
    setState((prev) => ({ ...prev, scripMapVersion: prev.scripMapVersion + 1 }))
  }, [])

  // ─── watchdog: forces a reconnect if no event has been seen for HEARTBEAT_TIMEOUT ───
  const resetWatchdog = useCallback(() => {
    if (watchdogTimeoutRef.current) clearTimeout(watchdogTimeoutRef.current)
    watchdogTimeoutRef.current = setTimeout(() => {
      console.warn("[WSProvider] Heartbeat watchdog timeout — reconnecting")
      if (wsRef.current) {
        try {
          wsRef.current.close()
        } catch {
          /* ignore */
        }
        wsRef.current = null
      }
      successfullySubscribedRef.current.clear()
      pendingSubscriptionsRef.current.clear()
      rejectedTokensRef.current.clear()
      pendingReqsRef.current.clear()
      setState((prev) => ({ ...prev, isConnected: false, isConnecting: false }))
      setRetryTrigger((p) => p + 1)
    }, HEARTBEAT_TIMEOUT)
  }, [])

  // ─── send a JSON envelope on the live WS, no-op if not OPEN ───
  const sendJson = useCallback((obj: any): boolean => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return false
    try {
      ws.send(JSON.stringify(obj))
      return true
    } catch (err) {
      console.warn("[WSProvider] send failed:", err)
      return false
    }
  }, [])

  // Flush the throttled tick batch: notify only the per-token listeners whose
  // tokens actually updated this window, then notify any "global" listeners
  // (useMarketData callers reading tickHash). This replaces the old
  // `setState({tickHash: ...})` bump that forced every useWS() consumer to
  // re-render at 5 Hz — now React is told *exactly* which subscribers saw new
  // data.
  const flushTickNotifications = useCallback(() => {
    const tokens = pendingTokenNotifyRef.current
    if (tokens.size === 0) return
    // Per-token notify — only subscribers for tokens that changed.
    tokens.forEach((token) => {
      const listeners = tickListenersRef.current.get(token)
      dbgToken(
        token,
        "flush: listeners.size=",
        listeners?.size ?? 0,
        "tickListeners map size=",
        tickListenersRef.current.size
      )
      if (listeners) listeners.forEach((cb) => cb())
    })
    tokens.clear()
    // Global notify — bumps version so useSyncExternalStore returns a new
    // snapshot to useMarketData() subscribers.
    anyTickVersionRef.current++
    anyTickListenersRef.current.forEach((cb) => cb())
    // Persist current ticks for subscribed tokens at ≤ 1 Hz so a hard
    // reload after the Connection Lost modal shows last-known LTPs on
    // the first paint instead of "—" placeholders. Throttled inline (not
    // via setTimeout) so the unmount path doesn't have to chase a pending
    // timer.
    const now = Date.now()
    if (now - lastTickPersistAtRef.current >= TICK_PERSIST_THROTTLE_MS) {
      lastTickPersistAtRef.current = now
      writeTickCache(ticksRef.current, subscribedTokensRef.current)
    }
  }, [])

  // ─── tick processor — shared by single-shot {type:"tick"} and batched {type:"ticks"} ───
  const ingestTicks = useCallback(
    (rawTicks: any[]) => {
      const now = Date.now()
      if (rawTicks.length > 0) lastTickAtRef.current = now
      for (const t of rawTicks) {
        if (!t || !t.token) continue
        // Normalize token to number. The wire shape from service-ws is always
        // numeric, but Postgres stores zerodha_instrument_token as VARCHAR and
        // the /api/admin/scrips response returns it as a string. Consumers
        // resolving via `scrip.zerodha_instrument_token` end up subscribing
        // with a string. We key tickListenersRef by number, so we MUST
        // normalize here or per-token listeners would never fire.
        const tokenNum = typeof t.token === "number" ? t.token : Number(t.token)
        if (!Number.isFinite(tokenNum) || tokenNum <= 0) continue
        ticksRef.current[tokenNum] = {
          symbol: t.Symbol || "",
          token: tokenNum,
          ltp: t.LTP || 0,
          bid: t.Bid || 0,
          ask: t.Ask || 0,
          open: t.Open || 0,
          high: t.High || 0,
          low: t.Low || 0,
          prevClose: t.Prev_Close || 0,
          volume: t.Volume || 0,
          time: t.Time || 0,
          change: parseFloat(t.change || "0") || 0,
          lastTradedQty: t.LastTradedQty || 0,
          atp: t.ATP || 0,
          oi: t.OI || 0,
          bidQty: t.BidQty || 0,
          askQty: t.AskQty || 0,
          lowerCircuit: t.LowerCircuit || 0,
          upperCircuit: t.UpperCircuit || 0,
          dayHigh: t.DayHigh || 0,
          dayLow: t.DayLow || 0,
          weekHigh: t.WeekHigh || 0,
          weekLow: t.WeekLow || 0,
        }
        pendingTokenNotifyRef.current.add(tokenNum)
        // Per-token freshness stamp for the gap-confirmed watchdog (see the 5s
        // resync). Uses the same numeric key as the subscription refs.
        perTokenLastTickRef.current.set(tokenNum, now)
        dbgToken(tokenNum, "tick arrived, queued for notify, ltp=", t.LTP)
      }

      // Evict ticks for unsubscribed tokens + enforce cap (defensive against
      // long-running tabs that have churned through many watchlists).
      const keys = Object.keys(ticksRef.current)
      if (keys.length > MAX_TICK_CACHE) {
        const subscribed = subscribedTokensRef.current
        for (const k of keys) {
          if (!subscribed.has(Number(k))) {
            delete ticksRef.current[Number(k)]
          }
        }
        const remaining = Object.keys(ticksRef.current)
        if (remaining.length > MAX_TICK_CACHE) {
          const toRemove = remaining.slice(0, remaining.length - MAX_TICK_CACHE)
          for (const k of toRemove) delete ticksRef.current[Number(k)]
        }
      }

      // 200 ms throttle: flush the accumulated per-token + global notifications
      // once per window. Identical cadence to the old tickHash bump, but the
      // wake-ups land on real subscribers instead of every useWS consumer.
      if (!throttleTimeoutRef.current) {
        throttleTimeoutRef.current = setTimeout(() => {
          throttleTimeoutRef.current = null
          flushTickNotifications()
        }, 200)
      }
    },
    [flushTickNotifications]
  )

  // ─── per-message-type handlers split out so the switch stays readable ───
  const onConnected = useCallback(
    (msg: any) => {
      console.log("[WSProvider] Server confirmed connection. ConnID:", msg.connId)
      successfullySubscribedRef.current.clear()
      pendingSubscriptionsRef.current.clear()
      pendingReqsRef.current.clear()
      setState((prev) => ({ ...prev, connId: msg.connId, isConnected: true }))

      // A server-confirmed socket is proof the session is valid RIGHT NOW, so
      // it is the strongest possible signal to re-arm a scrip map that never
      // loaded (provider mounted before the auth hint existed). No-ops when
      // the map is already healthy.
      ensureScripMapLoaded()

      const allTokens = Array.from(subscribedTokensRef.current)
      if (allTokens.length > 0) {
        // Replay every subscribed token on the fresh socket. The server will
        // ack with {type:"subscribed", subscribed, rejected, request_id}.
        const requestId = crypto.randomUUID()
        pendingReqsRef.current.set(requestId, { kind: "subscribe", tokens: allTokens })
        allTokens.forEach((t) => pendingSubscriptionsRef.current.add(t))
        sendJson({ type: "subscribe", tokens: allTokens, request_id: requestId })
      }
    },
    [sendJson, ensureScripMapLoaded]
  )

  const onSubscribed = useCallback((msg: any) => {
    const pending = msg.request_id ? pendingReqsRef.current.get(msg.request_id) : undefined
    if (!pending) return
    pendingReqsRef.current.delete(msg.request_id)

    if (msg.subscribed === 0 && msg.rejected > 0) {
      // Whole batch rejected — mark each so the 5 s resync doesn't retry storm.
      pending.tokens.forEach((t) => {
        rejectedTokensRef.current.add(t)
        successfullySubscribedRef.current.delete(t)
        pendingSubscriptionsRef.current.delete(t)
      })
      console.warn(`[WSProvider] All ${pending.tokens.length} tokens rejected by server`)
    } else {
      pending.tokens.forEach((t) => {
        successfullySubscribedRef.current.add(t)
        pendingSubscriptionsRef.current.delete(t)
      })
      if (msg.rejected > 0) {
        console.warn(
          `[WSProvider] ${msg.rejected} of ${pending.tokens.length} tokens rejected by server`
        )
      }
    }
  }, [])

  const onErrorMsg = useCallback((msg: any) => {
    // Error tied to a specific request — clean up the bookkeeping so the
    // resync timer can retry. Standalone errors (no request_id) are logged
    // but otherwise non-fatal.
    if (msg.request_id) {
      const pending = pendingReqsRef.current.get(msg.request_id)
      if (pending) {
        pendingReqsRef.current.delete(msg.request_id)
        pending.tokens.forEach((t) => pendingSubscriptionsRef.current.delete(t))
      }
    }
    console.warn(`[WSProvider] Server error ${msg.code}: ${msg.message}`)
  }, [])

  const onOrderUpdate = useCallback((data: any) => {
    if (!data) return
    const logicalHash = JSON.stringify({
      userid: data.userid,
      order_id: data.order_id,
      action: data.action,
      status: data.status,
    })
    if (logicalHash !== lastOrderEventHash.current) {
      lastOrderEventHash.current = logicalHash
      setState((prev) => ({ ...prev, lastOrderUpdate: Date.now() }))
    }
  }, [])

  const onUserUpdate = useCallback((data: any) => {
    if (!data) return
    // 'password_changed' fires when this user's login password was rotated
    // — by themselves on ANY device, or by a higher-privilege operator
    // (admin/broker resets a client, super-admin resets an admin, etc.).
    // The backend scopes the fan-out to (userid, role) so a numeric-id
    // collision across the users/brokers/admin tables can't leak this to
    // the wrong role. Immediately show the same SETTINGS UPDATED modal +
    // /login redirect the reactive HTTP 403 path shows — no wait for the
    // next request. `triggerSessionExpiry` is idempotent (guarded by a
    // module-level `_sessionExpiryHandled`), so a device that also
    // observes the 403 in the same window won't fire the modal twice.
    if (data.action === "password_changed") {
      triggerSessionExpiry({
        reason: "ws-password-changed",
        title: "Settings Updated",
        message: "Your account settings were updated. Please login again to continue.",
      })
      return
    }
    // 'config_updated' fires when admin/broker edits the trader's settings.
    // Routed to its own state field so screens can refetch on real config
    // changes without subscribing to the 1 Hz 'balance_updated' stream.
    if (data.action === "config_updated") {
      setState((prev) => ({ ...prev, lastConfigUpdate: Date.now() }))
      return
    }
    // 'payment_status_updated' is the push-delivered payment-gateway event
    // from backend/src/controllers/payment.controller.js#reconcileCompleted.
    // Carries reference_id + new status — listeners (Add Funds dialog,
    // pending-payment banner) react without polling.
    if (data.action === "payment_status_updated") {
      const status = (data.status as string) as PaymentEventStatus
      if (
        status === 'completed' ||
        status === 'failed' ||
        status === 'expired' ||
        status === 'cancelled'
      ) {
        setState((prev) => ({
          ...prev,
          lastPaymentEvent: {
            reference_id: String(data.reference_id),
            status,
            amount: Number(data.amount) || 0,
            ts: Date.now(),
          },
          // Also bump lastUserUpdate so the funds page re-fetches its balance —
          // funds-credit.service already publishes its own balance_updated,
          // but bumping here makes the dialog → page sync deterministic on
          // the same tick.
          lastUserUpdate: Date.now(),
          // Any topup terminal-flip also invalidates our "pending topups"
          // banner state — bump the topup tick so subscribers re-fetch.
          lastTopupEventTs: Date.now(),
        }))
      }
      return
    }

    // Topup lifecycle events — parent-child SwiftPay flow.
    //   child_topup_initiated → payer notified: a child started a topup
    //   own_topup_initiated   → payee's own peer tabs mirror pending state
    //   child_topup_settled   → payer notified: child's topup completed
    // All three drive the PendingTopupsBanner; a single tick counter is
    // enough — subscribers re-fetch on any bump.
    if (
      data.action === "child_topup_initiated" ||
      data.action === "own_topup_initiated" ||
      data.action === "child_topup_settled"
    ) {
      setState((prev) => ({ ...prev, lastTopupEventTs: Date.now() }))
      return
    }

    // Trader manual-deposit / withdrawal request lifecycle. Delivered to the
    // trader's direct broker AND (oversight permitting) the tenant admin, so
    // whichever of the two did NOT act sees the change immediately.
    //
    // This MUST be its own branch with its own tick and an early return. The
    // generic path below dedupes on {userid, action} against the previous
    // event, which would silently swallow two consecutive actions on two
    // different requests — precisely the case a shared queue produces.
    // Carrying request_id + status lets an open approve/reject dialog decide
    // whether the row it is displaying is the one that just changed.
    if (data.action === "request_queue_updated") {
      setState((prev) => ({
        ...prev,
        lastRequestQueueEventTs: Date.now(),
        lastRequestQueueEvent: {
          kind: String(data.kind || ""),
          request_id:
            data.request_id === null || data.request_id === undefined
              ? null
              : Number(data.request_id),
          status: data.status ? String(data.status) : null,
          ts: Date.now(),
        },
      }))
      return
    }

    // `time` (server-stamped, seconds) is included so two genuinely distinct
    // events of the same action for the same user — e.g. two separate
    // 'balance_updated' pushes later in the session — don't hash identically
    // and get silently dropped. Without it, only the FIRST 'balance_updated'
    // of a session ever bumped lastUserUpdate, since userid+action alone is
    // the same string for every later one — starving any long-lived listener
    // (one that doesn't remount on navigation, e.g. LowFundsStrip) of updates
    // past the first balance change.
    const logicalHash = JSON.stringify({ userid: data.userid, action: data.action, time: data.time })
    if (logicalHash !== lastUserEventHash.current) {
      lastUserEventHash.current = logicalHash
      setState((prev) => ({ ...prev, lastUserUpdate: Date.now() }))
    }
  }, [])

  const onNotification = useCallback((data: any) => {
    setState((prev) => ({ ...prev, lastNotification: data }))
    window.dispatchEvent(new CustomEvent("new-notification-received", { detail: data }))
  }, [])

  const onAuthExpired = useCallback((msg: any) => {
    console.warn("[WSProvider] Server signalled auth-expired:", msg?.reason)
    if (wsRef.current) {
      try {
        wsRef.current.close()
      } catch {
        /* ignore */
      }
      wsRef.current = null
    }
    // The WS heartbeat re-checks user state every ~60s; when a trader's
    // settings_version is bumped (or account blocked) while they're idle,
    // this is the ONLY signal that reaches the client — no HTTP request
    // fires. We must show the same modal + redirect the HTTP 403 path
    // does, otherwise the trader sees nothing. Route through the shared
    // helper, which also dispatches the session-expired CustomEvent for
    // ws-provider/layout listeners.
    const reason = msg?.reason
    if (reason === "SETTINGS_UPDATED") {
      triggerSessionExpiry({
        reason: "ws-settings-updated",
        title: "Settings Updated",
        message: "Your account settings were updated. Please login again to continue.",
      })
    } else if (reason === "ACCOUNT_BLOCKED") {
      triggerSessionExpiry({
        reason: "ws-account-blocked",
        title: "Account Blocked",
        message: "Your account has been blocked.",
      })
    } else {
      triggerSessionExpiry({ reason: `ws-${reason || "unknown"}` })
    }
  }, [])

  // =============================================================================
  // connect() — opens a fresh WebSocket and wires every event handler.
  // =============================================================================
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      console.log("[WSProvider] Already connecting — skipping duplicate connect()")
      return
    }
    if (wsRef.current) {
      try {
        wsRef.current.close()
      } catch {
        /* ignore */
      }
      // Detach the ref immediately. The browser fires `close` asynchronously,
      // so without this assignment the previous socket's onclose handler can
      // fire AFTER a replacement has been installed below, and the handler's
      // `wsRef.current = null` would clobber the live new socket's ref —
      // orphaning it (still streaming from the server, invisible to JS) and
      // triggering another retry → another socket. Ownership is then verified
      // again inside ws.onclose via `wsRef.current === ws`.
      wsRef.current = null
    }

    if (sessionExpiredRef.current) {
      console.log("[WSProvider] Session expired — refusing to reconnect")
      setState((prev) => ({ ...prev, isConnecting: false, isConnected: false }))
      return
    }
    if (typeof window !== "undefined" && !localStorage.getItem("user")) {
      console.log("[WSProvider] No active session, skipping connection")
      setState((prev) => ({ ...prev, isConnecting: false, isConnected: false }))
      return
    }
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/superadmin")) {
      console.log("[WSProvider] Superadmin flow, skipping WS connection")
      setState((prev) => ({ ...prev, isConnecting: false, isConnected: false }))
      return
    }

    const requestId = (() => {
      try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
          return crypto.randomUUID()
        }
      } catch {
        /* ignore */
      }
      return `ws-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`
    })()
    const url = buildWsUrl(requestId)
    if (!url) return

    console.log("[WSProvider] Connecting to:", url, "request_id:", requestId)
    setState((prev) => ({ ...prev, isConnecting: true }))

    const ws = new WebSocket(url)
    wsRef.current = ws

    ws.onopen = () => {
      console.log("[WSProvider] Socket opened")
      sessionProbedRef.current = false
      setState((prev) => ({ ...prev, isConnecting: false, retryCount: 0 }))
      resetWatchdog()
      // Note: isConnected is set true inside onConnected when the server
      // confirms with the {type:"connected"} envelope — that's when we know
      // the auth + session resolution actually succeeded.
    }

    ws.onmessage = (event) => {
      resetWatchdog()
      let msg: any
      try {
        if (typeof event.data !== "string") return
        msg = JSON.parse(event.data)
      } catch (err) {
        console.error("[WSProvider] message parse error:", err)
        return
      }

      switch (msg.type) {
        case "connected":
          onConnected(msg)
          break
        case "tick":
          {
            const tdbgRaw =
              typeof window !== "undefined" ? (window as any).__WS_DEBUG_TOKEN__ : undefined
            if (tdbgRaw != null && msg.data && Number(msg.data.token) === Number(tdbgRaw)) {
              dbgToken(Number(tdbgRaw), "WS frame: type=tick LTP=", msg.data.LTP)
            }
          }
          ingestTicks([msg.data])
          break
        case "ticks": {
          const arr = Array.isArray(msg.data) ? msg.data : []
          const tdbgRaw =
            typeof window !== "undefined" ? (window as any).__WS_DEBUG_TOKEN__ : undefined
          if (tdbgRaw != null) {
            const tdbg = Number(tdbgRaw)
            const hit = arr.find((t: any) => Number(t.token) === tdbg)
            if (hit) {
              dbgToken(
                tdbg,
                "WS frame: type=ticks (batch=",
                arr.length,
                ") contains our token LTP=",
                hit.LTP
              )
            }
          }
          ingestTicks(arr)
          break
        }
        case "order-update":
          onOrderUpdate(msg.data)
          break
        case "user-update":
          onUserUpdate(msg.data)
          break
        case "notification":
          onNotification(msg.data)
          break
        case "heartbeat":
          break // watchdog already reset above
        case "auth-expired":
          onAuthExpired(msg)
          break
        case "subscribed":
          onSubscribed(msg)
          break
        case "unsubscribed":
          /* server ack, no UI state to change */ break
        case "pong":
          break
        case "error":
          onErrorMsg(msg)
          break
        default:
          console.warn("[WSProvider] Unknown message type:", msg.type)
      }
    }

    ws.onerror = (err) => {
      // WS gives no status; treat as transport-level failure. The onclose
      // that follows handles reconnect scheduling.
      console.error("[WSProvider] Socket error:", err)
    }

    ws.onclose = (event) => {
      // Ownership check. `ws` is captured by closure as the socket this
      // handler was bound to. If `wsRef.current` no longer points at it,
      // a newer socket has already taken over (forced reconnect, watchdog,
      // visibility/online handler, etc.) — this is an orphan close and must
      // not touch `wsRef`, must not schedule a retry, must not flip state.
      // Without this guard, an in-flight close ack racing a new socket would
      // (a) null out the live ref, (b) trigger another connect(), and (c)
      // leave the previous "new" socket alive but unreferenced — the symptom
      // the user sees as multiple live WS connections in DevTools.
      const isStillOwned = wsRef.current === ws
      console.warn(
        `[WSProvider] Socket closed (code=${event.code} reason=${event.reason || "—"}) owned=${isStillOwned}`
      )
      if (!isStillOwned) {
        return
      }
      wsRef.current = null

      // Already known-expired (event listener flipped the flag) — don't loop.
      if (sessionExpiredRef.current) {
        setState((prev) => ({ ...prev, isConnected: false, isConnecting: false, connId: null }))
        return
      }

      // Auth-expired close — let the dialog flow handle it; don't burn into a retry loop.
      // Note: this code path fires only when the server explicitly closes the
      // already-open WS with code 4001. When the cookie is stale at the HTTP
      // upgrade step (the laptop-sleep scenario), the browser delivers code
      // 1006 (abnormal closure) instead — handled by the retry block below
      // plus the first-failure session probe.
      if (event.code === 4001) {
        // Same flow as onAuthExpired (in case the close arrives without the
        // preceding "auth-expired" message). triggerSessionExpiry is
        // idempotent — a no-op if onAuthExpired already fired.
        const reason = event.reason
        if (reason === "SETTINGS_UPDATED") {
          triggerSessionExpiry({
            reason: "ws-close-settings-updated",
            title: "Settings Updated",
            message: "Your account settings were updated. Please login again to continue.",
          })
        } else if (reason === "ACCOUNT_BLOCKED") {
          triggerSessionExpiry({
            reason: "ws-close-account-blocked",
            title: "Account Blocked",
            message: "Your account has been blocked.",
          })
        } else {
          triggerSessionExpiry({ reason: `ws-close-${reason || "auth-expired"}` })
        }
        setState((prev) => ({ ...prev, isConnected: false, isConnecting: false, connId: null }))
        return
      }

      setState((prev) => {
        const nextRetry = prev.retryCount + 1
        const delay = Math.min(INITIAL_RETRY_DELAY * Math.pow(2, nextRetry - 1), MAX_RETRY_DELAY)
        console.log(`[WSProvider] Retry #${nextRetry} in ${delay}ms`)

        // Probe a protected endpoint on the FIRST failure (was 2nd). The
        // api.ts 401 handler will dispatch `session-expired` if the cookie
        // has gone stale, and our listener above stops the retry loop.
        // Surfacing the dialog ~8s earlier than the previous "after 2 fails"
        // wiring is what the user sees as the difference between "silent
        // reconnect storm" and "clear Session Expired modal".
        //
        // Re-probed every SESSION_PROBE_EVERY_N attempts thereafter. Now that
        // the retry loop no longer terminates itself, a one-shot probe would
        // leave a genuinely-expired session undetected forever: the very first
        // probe fails on the network (not with a 401), and nothing would ever
        // ask again. At the 30 s backoff ceiling this re-asks roughly every
        // 5 minutes — far under the `auth` zone's rate limit.
        if (
          (nextRetry === 1 && !sessionProbedRef.current) ||
          (nextRetry > 1 && nextRetry % SESSION_PROBE_EVERY_N === 0)
        ) {
          sessionProbedRef.current = true
          apiFetch("/api/auth/me", { skipErrorNotification: true }).catch(() => {})
        }

        // Journal the close against any outage a failed REQUEST has already
        // opened. Deliberately does not open one by itself: sockets close for
        // benign reasons constantly (205 times in one day for sank09, almost
        // all recovered on the next attempt), and treating every close as an
        // incident would bury the real ones.
        noteWsClose(event.code, nextRetry)

        // Backoff ceiling reached. We keep retrying — see the comment on
        // RECONNECT_BACKOFF_CEILING_ATTEMPTS for why this is no longer a
        // give-up — but record the crossing once so "the socket was down long
        // enough to matter" remains queryable.
        if (nextRetry === RECONNECT_BACKOFF_CEILING_ATTEMPTS) {
          console.warn(
            `[WSProvider] ${nextRetry} consecutive failures; continuing to retry at ${MAX_RETRY_DELAY}ms`
          )
          noteWsGaveUp(nextRetry)
        }

        if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = setTimeout(() => setRetryTrigger((p) => p + 1), delay)

        return {
          ...prev,
          isConnected: false,
          isConnecting: false,
          connId: null,
          retryCount: nextRetry,
        }
      })
    }
  }, [
    retryTrigger,
    resetWatchdog,
    sendJson,
    onConnected,
    ingestTicks,
    onOrderUpdate,
    onUserUpdate,
    onNotification,
    onAuthExpired,
    onSubscribed,
    onErrorMsg,
  ])

  useEffect(() => {
    connect()
    return () => {
      if (wsRef.current) {
        try {
          wsRef.current.close()
        } catch {}
        // Detach the ref synchronously. The browser will fire the matching
        // onclose later, but by then the ownership check inside ws.onclose
        // (`wsRef.current === ws`) will return false and skip retry — exactly
        // the desired behaviour for unmount + connect-identity-change cleanup.
        wsRef.current = null
      }
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current)
      if (throttleTimeoutRef.current) clearTimeout(throttleTimeoutRef.current)
      if (watchdogTimeoutRef.current) clearTimeout(watchdogTimeoutRef.current)
      if (reconnectDebounceRef.current) clearTimeout(reconnectDebounceRef.current)
    }
  }, [connect])

  // ─── Session-expired guard ────────────────────────────────────────────────
  // Without this, the WS provider keeps trying to reconnect even after
  // api.ts has already detected an expired cookie via a 401 on a regular API
  // call. That produced the reported symptom: dozens of `wss://…/api/ws`
  // requests pile up in DevTools Network tab after a laptop wakes from sleep,
  // while the user sees no "Session Expired" feedback. Listening for the same
  // custom event that api.ts dispatches keeps the two channels in sync.
  useEffect(() => {
    const handleSessionExpired = (e: Event) => {
      if (sessionExpiredRef.current) return
      const detail = (e as CustomEvent).detail || {}
      console.warn("[WSProvider] session-expired event received — stopping reconnect loop", detail)
      sessionExpiredRef.current = true
      setSessionExpired(true)
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = null
      }
      if (reconnectDebounceRef.current) {
        clearTimeout(reconnectDebounceRef.current)
        reconnectDebounceRef.current = null
      }
      if (wsRef.current) {
        try {
          wsRef.current.close()
        } catch {
          /* ignore */
        }
        wsRef.current = null
      }
      setState((prev) => ({
        ...prev,
        isConnected: false,
        isConnecting: false,
        connId: null,
      }))
    }
    window.addEventListener("session-expired", handleSessionExpired)
    return () => window.removeEventListener("session-expired", handleSessionExpired)
  }, [])

  // ─── Disconnected-WS session-health probe ─────────────────────────────────
  // Backstop for the "same trader logged in on multiple devices — one changes
  // their password, the others don't get kicked" scenario when Device B's WS
  // isn't delivering events.  Reasons Device B's WS might not push the
  // `password_changed` frame:
  //   - WS connection is broken (firewall, backgrounded tab throttling,
  //     idle-timed-out by an intermediary proxy)
  //   - WS retry loop's onclose probe already fired ONCE early (before the
  //     other device changed the password) and won't re-fire until reconnect
  //   - WS never connected at all (initial handshake refused)
  //
  // While `isConnected === false`, poll `/api/auth/me` every 20s. apiFetch's
  // 403-SETTINGS_UPDATED handler surfaces the "Settings Updated" modal + the
  // /login redirect exactly the same as the reactive path any other API call
  // would trigger. Cost: 1 request per 20s per DISCONNECTED trader/admin only
  // — negligible next to the ticks stream and the 60s WS server-side auth
  // recheck that runs when the socket IS up.
  //
  // No probe when WS is connected: the server's 60s auth-recheck (see
  // ws.controller.js) already sends an `auth-expired` frame if the version
  // mismatch is detected there.
  useEffect(() => {
    if (typeof window === "undefined") return
    if (state.isConnected) return
    if (!localStorage.getItem("user")) return
    if (sessionExpiredRef.current) return
    // Superadmin flow doesn't run WS — skip the probe too.
    if (window.location.pathname.startsWith("/superadmin")) return
    // Demo modes intentionally have no server session — probing would 401 and
    // fire the session-expired modal wrongly.
    if (
      sessionStorage.getItem("demo_mode") === "true" ||
      sessionStorage.getItem("admin_demo_mode") === "true"
    ) {
      return
    }

    const probe = () => {
      // Re-check the guards on every tick — user may have logged out, or WS
      // may have come back up between ticks.
      if (sessionExpiredRef.current) return
      if (!localStorage.getItem("user")) return
      // apiFetch's 403 handler will call triggerSessionExpiry on
      // SETTINGS_UPDATED / ACCOUNT_BLOCKED, and its 401 handler will do the
      // same for SESSION_EXPIRED. Fire-and-forget.
      apiFetch("/api/auth/me", { skipErrorNotification: true }).catch(() => {})
    }

    const id = setInterval(probe, 20000)
    return () => clearInterval(id)
  }, [state.isConnected])

  // =============================================================================
  // subscribeTokens / unsubscribeTokens — now send WS messages instead of
  // making HTTP POSTs. No clientId in the payload — the server knows which
  // socket the message came from.
  // =============================================================================
  // Read state via stateRef so this callback's identity is stable across
  // every state change. Stable identity is what makes the memoized context
  // value below actually stable on order/notification/connection re-renders.
  // Defined ABOVE subscribeTokens (rather than next to its fellow public
  // context methods) because subscribeTokens now calls it — see
  // requestConnectForDemand below. Body is unchanged; deps are still [] since
  // it only touches refs and setState.
  const reconnect = useCallback((opts?: { force?: boolean; resetBackoff?: boolean }) => {
    // Already open and healthy — skip unless caller wants a forced rebuild
    // (visibility wake-up after long sleep, online event after network swap).
    if (!opts?.force && wsRef.current?.readyState === WebSocket.OPEN) {
      console.log("[WSProvider] Reconnect skipped — connection already open")
      return
    }
    if (opts?.force && wsRef.current) {
      console.log("[WSProvider] Forced reconnect — closing presumed-stale WebSocket")
      try {
        wsRef.current.close()
      } catch {
        /* ignore */
      }
      wsRef.current = null
      successfullySubscribedRef.current.clear()
      pendingSubscriptionsRef.current.clear()
      rejectedTokensRef.current.clear()
      pendingReqsRef.current.clear()
    }

    if (reconnectDebounceRef.current) clearTimeout(reconnectDebounceRef.current)
    reconnectDebounceRef.current = setTimeout(() => {
      reconnectDebounceRef.current = null
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = null
      }
      console.log("[WSProvider] Manual reconnect executing")
      // `retryCount` is the record of how many consecutive attempts have
      // failed. It drives two things: the exponential backoff delay, and the
      // every-Nth-attempt session re-probe in the onclose handler.
      //
      // Clearing it means "treat the next attempt as a fresh start", which is
      // honest for the triggers that represent a genuine CHANGE in conditions —
      // the network came back, the tab woke up, the user pressed Try Again.
      // It is NOT honest for a demand kick: a consumer wanting ticks tells us
      // nothing new about reachability, and zeroing the counter there would
      // hold the delay at the low end of the curve forever (1s→2s→4s, reset,
      // repeat) for any trader who keeps navigating while disconnected —
      // roughly an order of magnitude more connect attempts against a server
      // that is, by hypothesis, already down. `resetBackoff: false` lets that
      // caller jump the queue (skip the pending retry timer and attempt NOW)
      // without also lying about the failure history.
      if (opts?.resetBackoff !== false) {
        setState((prev) => ({ ...prev, retryCount: 0 }))
      }
      setRetryTrigger((p) => p + 1)
    }, 300)
  }, [])

  // Reconnect because a CONSUMER IS ASKING FOR TICKS, as opposed to because a
  // socket closed or the tab woke up.
  //
  // Incident 2026-08-06 (trader 13375): he navigated client-side back to the
  // watchlist while his socket was already dead, removed one scrip and added
  // NATURALGAS, and the row showed an em-dash for 80 seconds. Server side was
  // clean throughout — scrip enabled, token publishing, every subscribe the
  // server actually received ack'd +1/1. The provider had simply banked the
  // intent and attempted nothing.
  //
  // WSProvider lives in the root layout, so a client-side route change never
  // remounts it, and all three existing reconnect triggers miss this case:
  // the onclose retry setTimeout is frozen along with a backgrounded mobile
  // tab, the `online` event never fires when navigator.onLine never flipped,
  // and visibilitychange needs the user to leave and come back. None of them
  // means "someone wants data now".
  //
  // Throttled rather than debounced-only: the 5s resync re-sends subscribes
  // that keep failing, so an unthrottled kick would become a connect attempt
  // every 5s per disconnected trader.
  const lastDemandKickAtRef = useRef<number>(0)
  const requestConnectForDemand = useCallback(
    (opts?: { force?: boolean }) => {
      if (sessionExpiredRef.current) return
      if (typeof window !== "undefined" && !localStorage.getItem("user")) return
      const now = Date.now()
      if (now - lastDemandKickAtRef.current < DEMAND_RECONNECT_THROTTLE_MS) return
      lastDemandKickAtRef.current = now
      console.log("[WSProvider] Subscription demand with no live socket — connecting")
      // resetBackoff: false — attempt now, but leave the failure counter alone.
      // See the block comment inside reconnect() for why demand must not zero
      // it: this path can fire every 10 s indefinitely, and it would otherwise
      // pin the backoff to the bottom of the curve for the whole outage.
      reconnect({ ...opts, resetBackoff: false })
    },
    [reconnect]
  )

  const subscribeTokens = useCallback(
    (tokens: number[]) => {
      // Canonical numeric token keys. Tokens reach here as number (parseInt'd
      // catalog fallback) OR string (Postgres VARCHAR zerodha_instrument_token
      // straight off the positions/orders API) depending on the producer.
      // Keying the refs by the raw value splits `13152770` and "13152770" into
      // two SameValueZero keys — so a refetch that flips a scrip's rep emits
      // unsubscribe([number]) racing a same-instrument subscribe(["string"]),
      // and when the unsubscribe lands last the token freezes (the tya1
      // incident). Coerce to Number at this single choke point so every ref op
      // and every emitted frame keys off the one numeric value the tick store
      // already uses. Drop NaN / non-positive so a bad token can never become a
      // ref key or a sent frame.
      const nums = tokens
        .map((t) => (typeof t === "number" ? t : Number(t)))
        .filter((t) => Number.isFinite(t) && t > 0)
      if (nums.length === 0) return
      // Refcount: each call increments per-token count. Only tokens whose
      // count just went from 0→1 need an actual server subscribe; the rest
      // are already streaming for another consumer.
      const firstTimeTokens: number[] = []
      nums.forEach((t) => {
        const prev = tokenRefCountRef.current.get(t) ?? 0
        tokenRefCountRef.current.set(t, prev + 1)
        if (prev === 0) firstTimeTokens.push(t)
      })
      const newTokens = firstTimeTokens.filter(
        (t) =>
          !successfullySubscribedRef.current.has(t) &&
          !pendingSubscriptionsRef.current.has(t) &&
          !rejectedTokensRef.current.has(t)
      )
      dbg(
        "subscribeTokens called: requested",
        nums.length,
        "filtered to newTokens",
        newTokens.length
      )
      if (newTokens.length === 0) return
      newTokens.forEach((t) => subscribedTokensRef.current.add(t))

      const { connId, isConnected } = stateRef.current
      if (connId && isConnected) {
        newTokens.forEach((t) => pendingSubscriptionsRef.current.add(t))
        const requestId = crypto.randomUUID()
        pendingReqsRef.current.set(requestId, { kind: "subscribe", tokens: newTokens })
        const ok = sendJson({ type: "subscribe", tokens: newTokens, request_id: requestId })
        if (!ok) {
          // Socket not open — remove from pending so the resync timer retries.
          pendingReqsRef.current.delete(requestId)
          newTokens.forEach((t) => pendingSubscriptionsRef.current.delete(t))
          // State said connected but the socket refused the frame, so our view
          // and reality disagree. A mobile tab frozen mid-session comes back
          // with readyState stale-OPEN and no onclose ever delivered; left
          // alone, the resync re-sends into that dead socket forever and the
          // trader never sees a price again. Force a rebuild to resolve it.
          requestConnectForDemand({ force: true })
        }
      } else {
        // No live socket and a consumer wants ticks right now. See the block
        // comment on requestConnectForDemand for why nothing else covers this.
        requestConnectForDemand()
      }
    },
    [sendJson, requestConnectForDemand]
  )

  const unsubscribeTokens = useCallback(
    (tokens: number[]) => {
      // Canonical numeric token keys — see subscribeTokens. Coercing here means
      // a flip's unsubscribe([number]) and the paired subscribe(["string"])
      // resolve to the SAME ref, so the refcount nets out and no stray
      // unsubscribe frame is ever sent while a live consumer still holds it.
      const nums = tokens
        .map((t) => (typeof t === "number" ? t : Number(t)))
        .filter((t) => Number.isFinite(t) && t > 0)
      if (nums.length === 0) return
      dbg("unsubscribeTokens called for", nums.length, "tokens")
      // Refcount: decrement per-token count. Only tokens whose count drops to
      // 0 are actually released — other consumers (e.g. positions / trades
      // tabs) may still need the same token's ticks streaming.
      // We intentionally do NOT delete ticksRef.current[t] when refcount hits
      // zero. Routes that briefly drop a subscription during navigation (e.g.
      // dashboard → trades → exit → dashboard) re-subscribe within
      // milliseconds, and a stale last-known LTP in the cache prevents the
      // row from flashing "0.00" between mount and first-tick-arrival. The
      // 5 Hz batch handler runs a defensive sweep that evicts cached ticks
      // for tokens still unsubscribed at the next batch boundary.
      const droppedTokens: number[] = []
      nums.forEach((t) => {
        const prev = tokenRefCountRef.current.get(t) ?? 0
        if (prev <= 1) {
          tokenRefCountRef.current.delete(t)
          droppedTokens.push(t)
          subscribedTokensRef.current.delete(t)
          successfullySubscribedRef.current.delete(t)
          pendingSubscriptionsRef.current.delete(t)
        } else {
          tokenRefCountRef.current.set(t, prev - 1)
        }
      })
      if (droppedTokens.length === 0) return
      const { connId, isConnected } = stateRef.current
      if (connId && isConnected) {
        const requestId = crypto.randomUUID()
        pendingReqsRef.current.set(requestId, { kind: "unsubscribe", tokens: droppedTokens })
        sendJson({ type: "unsubscribe", tokens: droppedTokens, request_id: requestId })
      }
    },
    [sendJson]
  )

  // ─── force-reconnect on network online + tab visibility regained ───
  const lastSessionProbeRef = useRef<number>(0)
  const hiddenSinceRef = useRef<number>(0)
  useEffect(() => {
    const handleOnline = () => {
      console.log("[WSProvider] Network came online — reconnecting WS")
      reconnect({ force: true })
      // The socket is not the only thing a network outage can strand. If the
      // scrip catalog never loaded (or ended in the error state), the trader
      // is staring at a spinner that no reconnect alone will clear.
      ensureScripMapLoaded()
    }
    // Desktop app-switching can restore focus without firing visibilitychange.
    // Only re-arms the scrip map — deliberately does NOT force a reconnect,
    // which visibilitychange already governs with its staleness heuristics.
    const handleFocus = () => {
      ensureScripMapLoaded()
    }
    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenSinceRef.current = Date.now()
        return
      }
      // Tab re-shown / PWA returned to foreground — recover a stranded or
      // failed scrip map before anything else.
      ensureScripMapLoaded()
      const hiddenFor = hiddenSinceRef.current ? Date.now() - hiddenSinceRef.current : 0
      hiddenSinceRef.current = 0
      // Force-reconnect when either:
      //   (a) the tab was hidden longer than STALE_AFTER_HIDDEN_MS, OR
      //   (b) the last tick is older than TICK_FRESHNESS_MS.
      // Case (b) catches the common "tab in background for 30-90s, socket
      // appears OPEN on return but no data is actually arriving" pattern that
      // otherwise required the user to refresh. lastTickAtRef=0 means we've
      // never received a tick — also stale, force-reconnect.
      const tickAge = lastTickAtRef.current ? Date.now() - lastTickAtRef.current : Infinity
      const shouldForce = hiddenFor > STALE_AFTER_HIDDEN_MS || tickAge > TICK_FRESHNESS_MS
      console.log(
        `[WSProvider] Tab visible after ${Math.round(hiddenFor / 1000)}s hidden, last tick ${Math.round(tickAge / 1000)}s ago — reconnect (force=${shouldForce})`
      )
      reconnect({ force: shouldForce })

      if (!localStorage.getItem("user")) return
      const now = Date.now()
      if (now - lastSessionProbeRef.current < 60_000) return
      lastSessionProbeRef.current = now
      apiV1Fetch("/api/notifications/unread-count", { skipErrorNotification: true }).catch(() => {})
    }
    window.addEventListener("online", handleOnline)
    window.addEventListener("focus", handleFocus)
    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("focus", handleFocus)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [reconnect, ensureScripMapLoaded])

  // ─── 5 s resync: catches subscribes that were lost mid-reconnect ───
  useEffect(() => {
    const isDemo =
      typeof window !== "undefined" && sessionStorage.getItem("admin_demo_mode") === "true"

    const timer = setInterval(
      () => {
        if (isDemo && subscribedTokensRef.current.size > 0) {
          const timestamp = Date.now()
          subscribedTokensRef.current.forEach((token) => {
            const current: MarketTick = ticksRef.current[token] || {
              symbol: "DEMO",
              token,
              ltp: 2500,
              bid: 2498,
              ask: 2502,
              open: 2450,
              high: 2600,
              low: 2400,
              prevClose: 2480,
              volume: 10000,
              time: Date.now(),
              change: 1.5,
              lastTradedQty: 10,
              atp: 2500,
              oi: 500,
              bidQty: 100,
              askQty: 100,
              lowerCircuit: 2200,
              upperCircuit: 2800,
            }
            const jitter = (Math.random() - 0.5) * 4
            const newLtp = +(current.ltp + jitter).toFixed(2)
            ticksRef.current[token] = {
              ...current,
              ltp: newLtp,
              bid: +(newLtp - 2).toFixed(2),
              ask: +(newLtp + 2).toFixed(2),
              time: timestamp,
              change: +(current.change + jitter / 10).toFixed(2),
            }
            pendingTokenNotifyRef.current.add(token)
          })
          // Demo path runs at 1.5 s so direct notify is fine — no extra throttle.
          flushTickNotifications()
          return
        }

        if (state.connId && state.isConnected) {
          // Periodically retry tokens the server previously rejected. Admin
          // may have re-enabled a scrip mid-session; without this the trader
          // would have to refresh the page to pick it up. We drop the
          // rejected marker so the next resync attempts a fresh subscribe;
          // if the server still rejects, the onSubscribed handler re-adds
          // it. Retry cadence ~1/min keeps server load negligible.
          if (
            rejectedTokensRef.current.size > 0 &&
            Date.now() - lastRejectedRetryAtRef.current > 60_000
          ) {
            lastRejectedRetryAtRef.current = Date.now()
            const retryTokens = Array.from(rejectedTokensRef.current).filter((t) =>
              subscribedTokensRef.current.has(t)
            )
            if (retryTokens.length > 0) {
              console.log("[WSProvider] Retrying", retryTokens.length, "previously-rejected tokens")
              retryTokens.forEach((t) => rejectedTokensRef.current.delete(t))
            }
          }
          const missing = Array.from(subscribedTokensRef.current).filter(
            (t) =>
              !successfullySubscribedRef.current.has(t) &&
              !pendingSubscriptionsRef.current.has(t) &&
              !rejectedTokensRef.current.has(t)
          )
          if (missing.length > 0) {
            console.log("[WSProvider] Syncing", missing.length, "missed subscriptions")
            subscribeTokens(missing)
          }

          // ─── gap-confirmed per-token freshness watchdog ───
          // Silently recover a single token that stopped ticking while the
          // socket and other tokens stayed live (the tya1 freeze). We only act
          // when the overall stream is provably alive — some token ticked
          // within STREAM_ACTIVE_MS — so a quiet market never triggers churn.
          // Recovery is an idempotent `subscribe` replay (no unsubscribe): the
          // server re-sends the token's latest cached tick, un-freezing it in
          // ~1s. Purely additive — it can only ever send extra subscribes.
          const now = Date.now()
          const streamAlive = now - lastTickAtRef.current < STREAM_ACTIVE_MS
          if (streamAlive) {
            const stale: number[] = []
            successfullySubscribedRef.current.forEach((t) => {
              if ((tokenRefCountRef.current.get(t) ?? 0) <= 0) return // no live consumer
              const lastTick = perTokenLastTickRef.current.get(t) ?? 0
              if (now - lastTick <= PER_TOKEN_STALE_MS) return // still fresh
              const lastResub = lastResubAtRef.current.get(t) ?? 0
              if (now - lastResub < RESUB_THROTTLE_MS) return // throttled
              stale.push(t)
            })
            if (stale.length > 0) {
              stale.forEach((t) => lastResubAtRef.current.set(t, now))
              console.log(
                "[WSProvider] Freshness watchdog re-subscribing",
                stale.length,
                "stalled token(s)"
              )
              const requestId = crypto.randomUUID()
              pendingReqsRef.current.set(requestId, { kind: "subscribe", tokens: stale })
              sendJson({ type: "subscribe", tokens: stale, request_id: requestId })
            }
          }
        }
      },
      isDemo ? 1500 : 5000
    )
    return () => clearInterval(timer)
  }, [state.connId, state.isConnected, subscribeTokens, flushTickNotifications, sendJson])

  // Stable external-store handle for tick subscriptions. useMemo with [] deps
  // because every operation reads/writes refs, not state. Putting this in the
  // context value lets `useTick(token)` and `useMarketData()` plug into
  // useSyncExternalStore without re-subscribing on every render.
  const tickStore = useMemo<TickStore>(
    () => ({
      subscribe(token, cb) {
        // Normalize to number — see ingestTicks for the why. Callers passing
        // `scrip.zerodha_instrument_token` get a string from the VARCHAR
        // Postgres column; we coerce so subscribe/getSnapshot/notify all key
        // off the same numeric value.
        const key = typeof token === "number" ? token : Number(token)
        if (!Number.isFinite(key) || key <= 0) return () => {}
        let listeners = tickListenersRef.current.get(key)
        if (!listeners) {
          listeners = new Set()
          tickListenersRef.current.set(key, listeners)
        }
        listeners.add(cb)
        dbgToken(
          key,
          "tickStore.subscribe → listener REGISTERED, total listeners for token:",
          listeners.size
        )
        return () => {
          const set = tickListenersRef.current.get(key)
          if (!set) return
          set.delete(cb)
          dbgToken(key, "tickStore unsubscribe → listener REMOVED, remaining:", set.size)
          if (set.size === 0) tickListenersRef.current.delete(key)
        }
      },
      getSnapshot(token) {
        const key = typeof token === "number" ? token : Number(token)
        if (!Number.isFinite(key) || key <= 0) return undefined
        return ticksRef.current[key]
      },
      subscribeAny(cb) {
        anyTickListenersRef.current.add(cb)
        return () => {
          anyTickListenersRef.current.delete(cb)
        }
      },
      getAnySnapshot() {
        return anyTickVersionRef.current
      },
      // Stable handle to the mutable map. The ref reference itself never
      // changes; only its contents do (in place, via ingestTicks). Consumers
      // detect changes through subscribe()/getSnapshot() — which is exactly
      // the external-store contract useSyncExternalStore is built around. The
      // react-hooks/refs lint rule fires here because it can't tell a
      // stable-reference-with-mutable-contents pattern from a primitive read.

      ticks: ticksRef.current,
    }),
    []
  )

  // Memoize the context value so its identity is stable across renders. Only
  // changes when one of the state fields actually changes (rare events like
  // connect/disconnect, notification arrival, order/user update). Tick batches
  // no longer touch React state, so `tickHash`-shaped re-renders are gone from
  // the useWS() fan-out — they're delivered point-to-point via tickStore.
  //
  // The ref reads below expose stable references (the ref's `.current` is the
  // same object across renders; its contents are mutated in place). Consumers
  // observe changes through scripMapVersion / tickStore subscriptions, not
  // through identity. Same pattern as the original inline JSX value object.
  const contextValue = useMemo<WSContextType>(
    () => ({
      ...state,

      ticks: ticksRef.current,

      scripTokenMap: scripTokenMapRef.current,

      allScrips: allScripsRef.current,
      tickStore,
      refetchScrips,
      injectScrips,
      subscribeTokens,
      unsubscribeTokens,
      reconnect,
    }),
    [state, tickStore, refetchScrips, injectScrips, subscribeTokens, unsubscribeTokens, reconnect]
  )

  return (
    // Transitive react-hooks/refs warning: contextValue's useMemo includes
    // ref-backed stable references (see comment above). Suppressed for the
    // same reason — external-store pattern, not a primitive ref read.

    <WSContext.Provider value={contextValue}>
      {/* Banner reserved for the market-data-error case ONLY. The
          session-expired path renders the SESSION EXPIRED modal via
          triggerSessionExpiry() — showing both UI elements at once was the
          "two dialogs" duplication users were seeing. */}
      {state.scripMapError && !sessionExpired && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 60,
            background: "#dc2626",
            color: "white",
            padding: "calc(env(safe-area-inset-top, 0px) + 8px) 16px 8px",
            textAlign: "center",
            fontSize: "14px",
            fontWeight: 500,
            display: "flex",
            gap: 12,
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <span>Market data unavailable.</span>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              background: "white",
              color: "#dc2626",
              border: 0,
              borderRadius: 6,
              padding: "4px 12px",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Try Again
          </button>
        </div>
      )}
      {children}
    </WSContext.Provider>
  )
}

export function useWS() {
  const context = useContext(WSContext)
  if (context === undefined) {
    throw new Error("useWS must be used within a WSProvider")
  }
  return context
}
